import { useEffect, useState } from "react";
import { fetchAllFileCategories } from "services/files-category.service";
import { fetchFilesTypes } from "services/files-types.service";
import { fetchFileTemplates } from "services/files-templates.service";

type NamesById = Record<string, string>;

type Names = {
  categories: NamesById;
  fileTypes: NamesById;
  templates: NamesById;
};

function namesById(rows: any[], nameField: string): NamesById {
  return Object.fromEntries(rows.map((row) => [row.id, row[nameField]]));
}

/**
 * The words behind the ids a company keeps in its settings (a file category,
 * a file type, a document template), so a row can say "Customers" instead of
 * an id. `ready` turns true once the lists have loaded; a list that fails to
 * load only leaves its names empty.
 */
export function useSettingNames(companyId: string) {
  const [names, setNames] = useState<Names | null>(null);

  useEffect(() => {
    let unmounted = false;
    Promise.allSettled([
      fetchAllFileCategories(companyId),
      fetchFilesTypes(companyId),
      fetchFileTemplates(companyId),
    ]).then(([categories, fileTypes, templates]) => {
      if (unmounted) return;
      setNames({
        categories:
          categories.status === "fulfilled"
            ? namesById(categories.value, "title")
            : {},
        fileTypes:
          fileTypes.status === "fulfilled"
            ? namesById(fileTypes.value, "label")
            : {},
        templates:
          templates.status === "fulfilled"
            ? namesById(templates.value, "name")
            : {},
      });
    });
    return () => {
      unmounted = true;
    };
  }, [companyId]);

  return {
    ready: names !== null,
    categoryName: (id: string) => names?.categories[id] ?? "",
    fileTypeName: (id: string) => names?.fileTypes[id] ?? "",
    templateName: (id: string) => names?.templates[id] ?? "",
  };
}

export type SettingNames = ReturnType<typeof useSettingNames>;
